import React, { useState, useContext, useEffect, useCallback } from "react";
import '../../styles/page-titles.css'
import PermissionGate from "../../services/PermissionGate";
import styled from 'styled-components';
import Loading from "react-loading";

import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { IoSpeedometerOutline } from 'react-icons/io5';
import { formatPriceWithSymbol } from '../../utils/currency';

import { SidebarContext } from "../../AppRoutes";
import { getPedidosByStatus, getPedidosFinalizadosHistorico } from "../../services/api";

import { nowTz, toTz } from '../../utils/timezone';
import { getStoredUser } from '../../utils/safeStorage';

import { Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend
} from 'chart.js';

const centerTextPlugin = {
    id: 'centerTextPlugin',
    beforeDraw: (chart) => {
        const ctx = chart.ctx;
        ctx.save();

        const quantidade = chart.data.datasets[0]?.data?.[0];
        const faturamento = chart.data.datasets[0]?.faturamento;

        if (faturamento === undefined) {
            ctx.restore();
            return;
        }

        const centerX = (chart.chartArea.left + chart.chartArea.right) / 2;
        const centerY = (chart.chartArea.top + chart.chartArea.bottom) / 2;

        ctx.font = 'bold 16px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(quantidade ?? 0, centerX, centerY + 40);

        ctx.font = 'bold 14px Arial';
        ctx.textBaseline = 'top';
        ctx.fillText(formatPriceWithSymbol(faturamento ?? 0), centerX, centerY + 50);

        ctx.restore();
    }
};

ChartJS.register(ArcElement, Tooltip, Legend, centerTextPlugin);

const Container = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: ${({ sidebar }) => (sidebar ? '250px' : '100px')};
    min-height: 100vh;
    width: auto;
    transition: margin-left 200ms ease;
    background-color: #ffffff;
    padding: 24px;

    @media (max-width: 880px) {
        margin-left: 0;
        padding: 16px;
    }
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
    flex-wrap: wrap;
    gap: 16px;

    @media (max-width: 600px) {
        gap: 12px;
    }
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    flex-direction: column;
    gap: 16px;

    span {
        color: #666;
        font-size: 14px;
    }
`;

const StatusCardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 24px;

    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
    }

    @media (max-width: 400px) {
        grid-template-columns: 1fr;
    }
`;

const StatusCard = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 16px 12px;
    color: white;
    border-radius: 12px;
    background: ${props => props.bg};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    span {
        font-size: 14px;
        font-weight: 500;
        text-align: center;
    }

    strong {
        font-size: 24px;
        font-weight: 700;
        margin-top: 6px;
    }

    @media (max-width: 500px) {
        padding: 12px 8px;

        span {
            font-size: 13px;
        }

        strong {
            font-size: 20px;
        }
    }
`;

const PerformanceCard = styled.div`
    background: white;
    border-radius: 16px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    padding: 24px;
    margin-bottom: 30px;
`;

const PeriodSelector = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
    flex-wrap: wrap;
    gap: 12px;

    span {
        font-size: 14px;
        color: #666;
        font-weight: 500;
    }

    .period-buttons {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
    }

    button {
        padding: 6px 14px;
        border: 1px solid #ddd;
        border-radius: 8px;
        background: #f8f9fa;
        color: #555;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        min-width: 80px;

        &:hover {
            background: rgba(49, 140, 213, 0.08);
            border-color: rgb(49, 140, 213);
            color: rgb(49, 140, 213);
        }

        &.ativo {
            background: rgb(49, 140, 213);
            color: white;
            border-color: rgb(49, 140, 213);
        }
    }

    @media (max-width: 600px) {
        flex-direction: column;
        align-items: flex-start;

        .period-buttons {
            width: 100%;
        }

        button {
            flex: 1;
            min-width: 0;
        }
    }
`;

const MetricsRow = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    margin-bottom: 28px;

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
        gap: 16px;
    }
`;

const MetricItem = styled.div`
    text-align: center;
    padding: 16px;
    border-radius: 12px;
    background: #f8f9fa;

    span {
        display: block;
        color: #666;
        font-size: 14px;
        margin-bottom: 4px;
    }

    strong {
        display: block;
        font-size: 24px;
        font-weight: 700;
        color: #333;
        margin-bottom: 4px;
    }

    small {
        font-size: 12px;
        font-weight: 600;
    }

    @media (max-width: 600px) {
        padding: 12px;

        strong {
            font-size: 20px;
        }
    }
`;

const ChartsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;

    @media (max-width: 1100px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 500px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
    }
`;

const ChartItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 16px 8px;
    border-radius: 12px;
    background: #f8f9fa;
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    .chart-wrapper {
        width: 100%;
        max-width: 180px;
        position: relative;
    }

    .chart-label {
        font-size: 14px;
        font-weight: 600;
        color: #333;
        --margin-top: -40px;
        position: relative;
        z-index: 1;
    }

    @media (max-width: 500px) {
        padding: 12px 6px;

        .chart-wrapper {
            max-width: 140px;
        }

        .chart-label {
            font-size: 12px;
            --margin-top: -30px;
        }
    }
`;

const Desempenho = () => {

    const userParse = getStoredUser();
    const userID = userParse?._id;

    const empresa = localStorage.getItem('empresa')
    const empresaParse = JSON.parse(empresa)
    const idEmpresa = empresaParse.id_empresa;
    const vinculo_empresa = empresaParse.cnpj;
    const { sidebar } = useContext(SidebarContext);
    const { t } = useTranslation('reports');

    const [pedidosEmAnalise, setPedidosEmAnalise] = useState([]);
    const [pedidosEmProducao, setPedidosEmProducao] = useState([]);
    const [pedidosEmEntrega, setPedidosEmEntrega] = useState([]);
    const [faturamentoTotalHoje, setFaturamentoTotalHoje] = useState(0);
    const [pedidosFinalizadosHistorico, setPedidosFinalizadosHistorico] = useState([]);
    const [pedidosEntregues, setPedidosEntregues] = useState([]);
    const [faturamentoTotalPedidosFinalizados, setFaturamentoTotalPedidosFinalizados] = useState(0);
    const [ticketMedioPedidos, setTicketMedioPedidos] = useState(0);
    const [percentualFaturamento, setPercentualFaturamento] = useState(0);
    const [percentualPedidos, setPercentualPedidos] = useState(0);
    const [percentualTicketMedio, setPercentualTicketMedio] = useState(0);
    const [isLoading, setIsLoading] = useState(true);

    const [periodo, setPeriodo] = useState('7dias');
    const [datePeriod, setDatePeriod] = useState(nowTz().subtract(6, 'days').format('DD/MM/YYYY'));

    // Dados para os gráficos de pedidos por modalidade
    const [data, setData] = useState({
        entregas: { quantidade: 0, faturamento: 0 },
        retiradas: { quantidade: 0, faturamento: 0 },
        mesa: { quantidade: 0, faturamento: 0 },
        taxa_entrega: { quantidade: 0, faturamento: 0 }
    });

    const commonOptions = {
        rotation: -90,
        circumference: 180,
        cutout: '80%',
        plugins: {
            legend: { display: false },
            tooltip: { enabled: true }
        },
        animation: { animateRotate: true },
        elements: { arc: { borderWidth: 0 } }
    };

    const optionsEntregaRetirada = {
        ...commonOptions,
        plugins: {
            ...commonOptions.plugins,
            centerTextPlugin
        }
    };

    const handleChangePeriodo = (novoPeriodo) => {
        setPeriodo(novoPeriodo);
        if (novoPeriodo === 'hoje') {
            setDatePeriod(nowTz().format('DD/MM/YYYY'));
        }
        if (novoPeriodo === '7dias') {
            setDatePeriod(nowTz().subtract(6, 'days').format('DD/MM/YYYY'));
        }
        if (novoPeriodo === '15dias') {
            setDatePeriod(nowTz().subtract(14, 'days').format('DD/MM/YYYY'));
        }
        if (novoPeriodo === '30dias') {
            setDatePeriod(nowTz().subtract(29, 'days').format('DD/MM/YYYY'));
        }
    };

    const fetchData = useCallback(async () => {
        try {
            const forReport = true;
            const pedidosAnalise = await getPedidosByStatus(userID, idEmpresa, vinculo_empresa, '1');
            const pedidosProducao = await getPedidosByStatus(userID, idEmpresa, vinculo_empresa, '2');
            const pedidosEntrega = await getPedidosByStatus(userID, idEmpresa, vinculo_empresa, '3');
            const pedidosFinalizados = await getPedidosByStatus(userID, idEmpresa, vinculo_empresa, '4', forReport);

            setPedidosEmAnalise(pedidosAnalise.data.pedidos);
            setPedidosEmProducao(pedidosProducao.data.pedidos);
            setPedidosEmEntrega(pedidosEntrega.data.pedidos);

            let faturamentoTotal = 0;
            const hoje = nowTz().startOf('day');

            pedidosFinalizados.data.pedidos.forEach(pedido => {
                const createdAtDate = toTz(pedido.createdAt)
                if (createdAtDate.isSame(hoje, 'day') && pedido.valor_total !== undefined) {
                    faturamentoTotal += pedido.valor_total;
                }
            });

            process.env.REACT_APP_ISDEVELOP === 'true' && console.log("faturamentoTotal>", faturamentoTotal);
            setFaturamentoTotalHoje(faturamentoTotal);
        } catch (error) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.error('Erro ao buscar dados:', error);
            toast.error(t('common.fetchError'));
        }
    }, [userID, idEmpresa, vinculo_empresa, periodo]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        const fetchPedidosFinalizados = async () => {
            try {
                const response = await getPedidosFinalizadosHistorico(userID, idEmpresa, vinculo_empresa, '4', periodo);
                if (response.data.pedidos) {
                    const pedidos = response.data.pedidos;
                    const pedidosComparativos = response.data.pedidosComparativos;
                    setPedidosFinalizadosHistorico(pedidos);

                    let faturamentoTotalAtual = 0;
                    pedidos.forEach(pedido => {
                        if (pedido.valor_total !== undefined) {
                            faturamentoTotalAtual += pedido.valor_total;
                        }
                    });
                    const entregues = pedidos.filter(pedido => pedido.entrega?.tipo_entrega?.toLowerCase() === 'entrega');
                    const retirados = pedidos.filter(pedido => pedido.entrega?.tipo_entrega?.toLowerCase() === 'retirada');
                    const mesa = pedidos.filter(pedido => pedido.entrega?.tipo_entrega?.toLowerCase() === 'mesa' || pedido.origem_pedido === 'mesa');

                    setPedidosEntregues(entregues);

                    const faturamentoEntregues = entregues.reduce((total, pedido) => total + (pedido.valor_total || 0), 0);
                    const faturamentoRetirados = retirados.reduce((total, pedido) => total + (pedido.valor_total || 0), 0);
                    const faturamentoMesa = mesa.reduce((total, pedido) => total + (pedido.valor_total || 0), 0);
                    const faturamentoTaxa_entrega = entregues.reduce((total, pedido) => total + (pedido.entrega?.valor || 0), 0);

                    setData({
                        entregas: {
                            quantidade: entregues.length,
                            faturamento: faturamentoEntregues
                        },
                        retiradas: {
                            quantidade: retirados.length,
                            faturamento: faturamentoRetirados
                        },
                        mesa: {
                            quantidade: mesa.length,
                            faturamento: faturamentoMesa
                        },
                        taxa_entrega: {
                            quantidade: entregues.length,
                            faturamento: faturamentoTaxa_entrega
                        }
                    });

                    const ticketMedioAtual = pedidos.length ? (faturamentoTotalAtual / pedidos.length) : 0;

                    const hoje = nowTz().startOf('day');
                    pedidos.filter(pedido => {
                        const createdAtDate = toTz(pedido.createdAt);
                        return createdAtDate.isSame(hoje, 'day');
                    });

                    setFaturamentoTotalPedidosFinalizados(faturamentoTotalAtual);
                    setTicketMedioPedidos(ticketMedioAtual);

                    let faturamentoTotalComparativo = 0;
                    pedidosComparativos.forEach(pedido => {
                        if (pedido.valor_total !== undefined) {
                            faturamentoTotalComparativo += pedido.valor_total;
                        }
                    });

                    const ticketMedioComparativo = pedidosComparativos.length ? (faturamentoTotalComparativo / pedidosComparativos.length) : 0;

                    const percentualFaturamento = faturamentoTotalComparativo ? ((faturamentoTotalAtual - faturamentoTotalComparativo) / faturamentoTotalComparativo) * 100 : 100;
                    const percentualPedidos = pedidosComparativos.length ? ((pedidos.length - pedidosComparativos.length) / pedidosComparativos.length) * 100 : 100;
                    const percentualTicketMedio = ticketMedioComparativo ? ((ticketMedioAtual - ticketMedioComparativo) / ticketMedioComparativo) * 100 : 100;

                    setPercentualFaturamento(percentualFaturamento);
                    setPercentualPedidos(percentualPedidos);
                    setPercentualTicketMedio(percentualTicketMedio);
                }
            } catch (error) {
                process.env.REACT_APP_ISDEVELOP === 'true' && console.error('Erro ao buscar pedidos finalizados:', error);
                toast.error(t('common.fetchError'));
            } finally {
                setIsLoading(false);
            }
        };

        fetchPedidosFinalizados();
    }, [periodo, idEmpresa, userID, vinculo_empresa]);

    return (
        <>
            <PermissionGate permissions={['default']}>
                <Container sidebar={sidebar}>
                    <Header>
                        <div className="page-title-with-icon">
                            <div className="page-icon"><IoSpeedometerOutline /></div>
                            <h1 className="page-title">{t('performance.title')}</h1>
                        </div>
                    </Header>

                    {isLoading ? (
                        <LoadingContainer>
                            <Loading type="spin" color="rgb(49, 140, 213)" height={50} width={50} />
                            <span>{t('common.loadingData')}</span>
                        </LoadingContainer>
                    ) : (
                        <>
                            <StatusCardsGrid>
                                <StatusCard bg="#007bff">
                                    <span>{t('performance.todayRevenue')}</span>
                                    <strong>{formatPriceWithSymbol(faturamentoTotalHoje)}</strong>
                                </StatusCard>
                                <StatusCard bg="#ffc107">
                                    <span>{t('performance.inAnalysis')}</span>
                                    <strong>{pedidosEmAnalise.length}</strong>
                                </StatusCard>
                                <StatusCard bg="#fd7e14">
                                    <span>{t('performance.inProduction')}</span>
                                    <strong>{pedidosEmProducao.length}</strong>
                                </StatusCard>
                                <StatusCard bg="#28a745">
                                    <span>{t('performance.readyForDelivery')}</span>
                                    <strong>{pedidosEmEntrega.length}</strong>
                                </StatusCard>
                            </StatusCardsGrid>

                            <PerformanceCard>
                                <PeriodSelector>
                                    <span>{t('common.periodRange', { start: datePeriod, end: nowTz().format('DD/MM/YYYY') })}</span>
                                    <div className="period-buttons">
                                        <button type="button" onClick={() => handleChangePeriodo('hoje')} className={periodo === 'hoje' ? 'ativo' : ''}>{t('performance.today')}</button>
                                        <button type="button" onClick={() => handleChangePeriodo('7dias')} className={periodo === '7dias' ? 'ativo' : ''}>{t('performance.last7')}</button>
                                        <button type="button" onClick={() => handleChangePeriodo('15dias')} className={periodo === '15dias' ? 'ativo' : ''}>{t('performance.last15')}</button>
                                        <button type="button" onClick={() => handleChangePeriodo('30dias')} className={periodo === '30dias' ? 'ativo' : ''}>{t('performance.last30')}</button>
                                    </div>
                                </PeriodSelector>

                                <MetricsRow>
                                    <MetricItem>
                                        <span>{t('common.revenue')}</span>
                                        <strong>{formatPriceWithSymbol(faturamentoTotalPedidosFinalizados)}</strong>
                                        <small style={{ color: percentualFaturamento >= 0 ? '#28a745' : '#dc3545' }}>
                                            {percentualFaturamento >= 0 ? `▲ ${percentualFaturamento.toFixed(2)}%` : `▼ ${percentualFaturamento.toFixed(2)}%`}
                                        </small>
                                    </MetricItem>
                                    <MetricItem>
                                        <span>{t('common.orders')}</span>
                                        <strong>{pedidosFinalizadosHistorico.length}</strong>
                                        <small style={{ color: percentualPedidos >= 0 ? '#28a745' : '#dc3545' }}>
                                            {percentualPedidos >= 0 ? `▲ ${percentualPedidos.toFixed(2)}%` : `▼ ${percentualPedidos.toFixed(2)}%`}
                                        </small>
                                    </MetricItem>
                                    <MetricItem>
                                        <span>{t('common.avgTicket')}</span>
                                        <strong>{formatPriceWithSymbol(ticketMedioPedidos)}</strong>
                                        <small style={{ color: percentualTicketMedio >= 0 ? '#28a745' : '#dc3545' }}>
                                            {percentualTicketMedio >= 0 ? `▲ ${percentualTicketMedio.toFixed(2)}%` : `▼ ${percentualTicketMedio.toFixed(2)}%`}
                                        </small>
                                    </MetricItem>
                                </MetricsRow>

                                <ChartsGrid>
                                    <ChartItem>
                                        <div className="chart-wrapper">
                                            <Doughnut data={{
                                                datasets: [{
                                                    data: [data.entregas.quantidade, pedidosFinalizadosHistorico.length - data.entregas.quantidade],
                                                    backgroundColor: ['#007bff', '#e9ecef'],
                                                    faturamento: data.entregas.faturamento
                                                }]
                                            }} options={optionsEntregaRetirada} />
                                        </div>
                                        <span className="chart-label">{t('common.delivery')}</span>
                                    </ChartItem>
                                    <ChartItem>
                                        <div className="chart-wrapper">
                                            <Doughnut data={{
                                                datasets: [{
                                                    data: [data.retiradas.quantidade, pedidosFinalizadosHistorico.length - data.retiradas.quantidade],
                                                    backgroundColor: ['#28a745', '#e9ecef'],
                                                    faturamento: data.retiradas.faturamento
                                                }]
                                            }} options={optionsEntregaRetirada} />
                                        </div>
                                        <span className="chart-label">{t('common.pickup')}</span>
                                    </ChartItem>
                                    <ChartItem>
                                        <div className="chart-wrapper">
                                            <Doughnut data={{
                                                datasets: [{
                                                    data: [data.mesa.quantidade, pedidosFinalizadosHistorico.length - data.mesa.quantidade],
                                                    backgroundColor: ['#ff9800', '#e9ecef'],
                                                    faturamento: data.mesa.faturamento
                                                }]
                                            }} options={optionsEntregaRetirada} />
                                        </div>
                                        <span className="chart-label">{t('common.table')}</span>
                                    </ChartItem>
                                    <ChartItem>
                                        <div className="chart-wrapper">
                                            <Doughnut data={{
                                                datasets: [{
                                                    data: [data.taxa_entrega.quantidade, pedidosFinalizadosHistorico.length - data.taxa_entrega.quantidade],
                                                    backgroundColor: ['#dc3545', '#e9ecef'],
                                                    faturamento: data.taxa_entrega.faturamento
                                                }]
                                            }} options={optionsEntregaRetirada} />
                                        </div>
                                        <span className="chart-label">{t('common.deliveryFee')}</span>
                                    </ChartItem>
                                </ChartsGrid>
                            </PerformanceCard>
                        </>
                    )}
                </Container>
            </PermissionGate>
        </>
    )
}

export default Desempenho;
